// LocalBusinessSchema
// -------------------
// Fetches the admin-editable brand settings once at page load and injects a
// <script type="application/ld+json"> LocalBusiness block into <head>. If the
// fetch fails (network, backend down), the fallback JSON-LD in
// public/index.html still ships correct info — this hook only UPGRADES it
// with the latest admin edits.
//
// Google's crawler executes JS on modern sites (since ~2020) and picks up
// dynamically-injected structured data on the second-pass render, so this
// keeps our schema in sync with Settings without a redeploy.

import { useEffect } from "react";
import { api } from "../lib/api";

const ORIGIN = "https://www.koltprinting.com";
const SCRIPT_ID = "kolt-localbusiness-jsonld";

function buildSchema(s) {
  const graph = [
    {
      "@type":       ["LocalBusiness", "PrintShop"],
      "@id":         `${ORIGIN}/#organization`,
      name:          s.name || "Kolt Printing",
      legalName:     "Koltprinting LLC",
      url:           `${ORIGIN}/`,
      logo:          `${ORIGIN}/og-cover.png`,
      image:         `${ORIGIN}/og-cover.png`,
      slogan:        "Custom printing you'll actually love.",
      address: {
        "@type":          "PostalAddress",
        // Empty strings are worse than omitted keys in structured data — we
        // publish the production city only (no walk-in street address).
        ...(s.street_address ? { streetAddress: s.street_address } : {}),
        addressLocality:  s.address_locality,
        addressRegion:    s.address_region,
        ...(s.postal_code ? { postalCode: s.postal_code } : {}),
        addressCountry:   s.address_country,
      },
      telephone:  s.telephone,
      email:      s.email,
      priceRange: "$$",
      geo: (s.latitude != null && s.longitude != null) ? {
        "@type":  "GeoCoordinates",
        latitude: s.latitude,
        longitude: s.longitude,
      } : undefined,
      openingHoursSpecification: [{
        "@type":   "OpeningHoursSpecification",
        dayOfWeek: s.days_open || [],
        opens:     s.opens,
        closes:    s.closes,
      }],
      sameAs: (s.same_as || []).filter(Boolean),
      areaServed: [
        { "@type": "City",  name: "Houston" },
        { "@type": "State", name: "Texas" },
        { "@type": "Country", name: "United States" },
      ],
    },
  ];
  return { "@context": "https://schema.org", "@graph": graph };
}

export default function LocalBusinessSchema() {
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/brand-settings");
        if (cancelled || !data) return;
        // Replace (or create) the LocalBusiness JSON-LD in <head>. We use a
        // stable ID so subsequent updates upgrade the same node instead of
        // stacking duplicates on client-side navigation.
        let el = document.getElementById(SCRIPT_ID);
        if (!el) {
          el = document.createElement("script");
          el.id = SCRIPT_ID;
          el.type = "application/ld+json";
          document.head.appendChild(el);
        }
        el.textContent = JSON.stringify(buildSchema(data));
      } catch {
        // Silent — the static fallback in public/index.html stays in place,
        // so search engines never see nothing.
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return null;
}
